import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import { API_BASE_URL } from '../config/api';
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  subscribeAccessToken,
} from '../auth/accessToken';
import type { UserPreferences } from '../types/userPreferences';

interface User {
  id: string;
  username: string;
  name: string;
  role: string;
  // Optional multi-role support — backend may return both `role` (legacy
  // single string) and `roles` (new array). PermissionContext prefers the
  // array when present and falls back to the single role.
  roles?: string[];
  ui_preferences?: UserPreferences;
}

interface AuthContextType {
  isAuthenticated: boolean;
  userName: string | null;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * The access token now lives in memory only (see ../auth/accessToken).
 * Persistence across page reloads is achieved by calling /auth/refresh,
 * which reads the HTTP-only refresh cookie that JavaScript cannot touch.
 *
 * The `user` object (id / username / role / display name) is intentionally
 * not persisted in localStorage either: we get it back from /auth/refresh
 * so the canonical source is always the server. This avoids the old class
 * of bugs where the user's role array drifted between the JWT and the
 * cached localStorage copy.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bootstrap: on first mount, try to silently restore the session from
  // the HTTP-only refresh cookie. If the cookie is present and valid,
  // /auth/refresh returns a fresh access token and the user payload; if
  // not, we land in the unauthenticated state and the login screen takes
  // over. Either way we set isLoading=false so the router can render.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        if (cancelled) {
          return;
        }

        const { accessToken, token, user: userData } = response.data;
        const minted = accessToken ?? token;
        if (minted && userData) {
          setAccessToken(minted);
          setIsAuthenticated(true);
          setUserName(userData.name || userData.username);
          setUser(userData);
          Sentry.setUser({ id: userData.id, username: userData.username });
        }
      } catch {
        // No valid refresh cookie. Stay in unauthenticated state without
        // surfacing an error — this is the normal "first visit" path.
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep React state in sync if the access token is cleared from outside
  // React (e.g. axios interceptor giving up after a failed refresh).
  useEffect(() => {
    return subscribeAccessToken(token => {
      if (token === null) {
        setIsAuthenticated(false);
        setUserName(null);
        setUser(null);
        Sentry.setUser(null);
      }
    });
  }, []);

  const login = async (username: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { username, password },
        { withCredentials: true }
      );

      const { accessToken, token, user: userData } = response.data;
      const minted = accessToken ?? token;
      if (!minted) {
        throw new Error('Login response missing access token');
      }

      setAccessToken(minted);
      setIsAuthenticated(true);
      setUserName(userData.name || userData.username);
      setUser(userData);
      setError(null);
      Sentry.setUser({ id: userData.id, username: userData.username });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Tell the server to revoke the refresh token before we clear local
    // state. We swallow errors here so a network blip on logout still
    // results in the user being signed out from the UI's perspective —
    // the next /refresh will fail anyway and the cookie will get
    // expired by the browser eventually.
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Intentionally ignored; see comment above.
    }

    clearAccessToken();
    setIsAuthenticated(false);
    setUserName(null);
    setUser(null);
    setError(null);
    Sentry.setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, userName, user, login, logout, isLoading, error }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// Re-export for callers that need to inspect the current access token
// without going through the React context. Notably used by the axios
// client and by the `getFetchAuthHeaders` helper.
export { getAccessToken };
